import { MessageType } from '@chessclub/proto-ts/index.websocket_gateway'
import { Action, action, thunk, Thunk } from 'easy-peasy'
import { positionToFEN } from '../../chess/fen'
import { createRootGameTree, getStartingPosition, undoLastMove } from '../../chess/gameTree'
import { BasePositionFEN } from '../../chess/positionPresets'
import { Color, GameTree } from '../../chess/types'
import { ENABLE_DAILY_PUZZLE_FEEDBACK } from '../../env'
import { Arrow, Square } from '../../react-chessboard/src/chessboard/types'
import { analyticsManager } from '../../sharedComponents/src/globalHeader/services/analytics/AnalyticsManager'
import { StoreModel } from '../../store/store'
import { usedHint } from './functions/puzzlesApi'

interface Validations {
    prevDate: boolean
    nextDate: boolean
}

export type DailyPuzzleModel = {
    // State
    puzzleId?: string
    flipped: boolean
    puzzleGameTree: GameTree
    progressCurrentPositionId: string
    gameTree: GameTree
    currentPositionId: string
    myColor: Color
    colorToMove: number
    userRating: number
    validations: Validations
    hint?: Square
    hintsCounter: number
    availableHints: number
    customArrows: Array<Arrow>
    showStreak: boolean
    streak: number
    showFeedbackDialog: boolean
    feedbackMessage: string
    showStreakAnimation: boolean

    // Actions
    setGameTree: Action<DailyPuzzleModel, GameTree>
    setProgressCurrentPositionId: Action<DailyPuzzleModel, string>
    setCurrentPositionId: Action<DailyPuzzleModel, string>
    setFlipBoard: Action<DailyPuzzleModel, boolean>
    initialiseGame: Action<
        DailyPuzzleModel,
        { puzzleId: string; gameTree?: GameTree | undefined; isFirstMove?: boolean | undefined }
    >
    setColorToMove: Action<DailyPuzzleModel, number>
    undoLastMove: Action<DailyPuzzleModel, GameTree>
    resetGame: Action<DailyPuzzleModel>
    setUserRating: Action<DailyPuzzleModel, number>
    setValidations: Action<DailyPuzzleModel, Validations>
    setHint: Action<DailyPuzzleModel, { from: Square; to?: Square } | undefined>
    setHintAsync: Thunk<DailyPuzzleModel, { from: Square; to?: Square } | undefined>
    clearHint: Action<DailyPuzzleModel>
    setAvailableHints: Action<DailyPuzzleModel, number>
    setShowStreak: Action<DailyPuzzleModel, boolean>
    setStreak: Action<DailyPuzzleModel, number>
    setShowFeedbackDialog: Action<DailyPuzzleModel, boolean>
    setFeedbackMessage: Action<DailyPuzzleModel, string>
    setShowStreakAnimation: Action<DailyPuzzleModel, boolean>

    // Thunks
    establishListener: Thunk<DailyPuzzleModel, undefined, undefined, StoreModel>
}

const initialGameTree = createRootGameTree(BasePositionFEN)
const initialCurrentPosition = getStartingPosition(initialGameTree).id

const DailyPuzzleViewModel: DailyPuzzleModel = {
    // Default State
    myColor: Color.White,
    flipped: false,
    puzzleGameTree: initialGameTree,
    progressCurrentPositionId: initialCurrentPosition,
    gameTree: initialGameTree,
    currentPositionId: initialCurrentPosition,
    colorToMove: 1,
    userRating: 0,
    validations: { prevDate: true, nextDate: false },
    hintsCounter: 0,
    availableHints: -1,
    customArrows: [],
    showStreak: false,
    streak: 0,
    showFeedbackDialog: false,
    feedbackMessage: '',
    showStreakAnimation: false,

    // Actions

    setGameTree: action((state, payload) => {
        state.gameTree = payload
        state.currentPositionId = getStartingPosition(payload).id
    }),

    setProgressCurrentPositionId: action((state, payload) => {
        state.progressCurrentPositionId = payload
    }),

    setCurrentPositionId: action((state, payload) => {
        state.currentPositionId = payload
    }),

    setFlipBoard: action((state, flipped) => {
        state.flipped = flipped
    }),

    initialiseGame: action((state, gd) => {
        const newPuzzleGameTree = gd.gameTree || initialGameTree
        state.puzzleGameTree = newPuzzleGameTree
        const startingProgressPosition = getStartingPosition(newPuzzleGameTree)
        state.progressCurrentPositionId = startingProgressPosition.id

        const newGameTree = createRootGameTree(positionToFEN(startingProgressPosition.position))
        state.gameTree = newGameTree
        state.currentPositionId = getStartingPosition(newGameTree).id

        if (gd.isFirstMove) {
            state.myColor = startingProgressPosition.position.turn
        } else if (startingProgressPosition.position.turn === Color.Black) {
            state.myColor = Color.White
        } else {
            state.myColor = Color.Black
        }

        state.flipped = state.myColor === Color.Black
        state.puzzleId = gd.puzzleId
    }),

    setColorToMove: action((state, number) => {
        state.colorToMove = number
    }),

    undoLastMove: action((state, payload) => {
        const previousPositionId = undoLastMove(payload).id || initialCurrentPosition
        state.currentPositionId = previousPositionId
        state.gameTree = payload
    }),

    resetGame: action((state) => {
        state.myColor = Color.White
        state.puzzleGameTree = initialGameTree
        state.progressCurrentPositionId = initialCurrentPosition
        state.gameTree = initialGameTree
        state.currentPositionId = initialCurrentPosition
        state.colorToMove = 1
        state.puzzleId = undefined
    }),

    setUserRating: action((state, rating) => {
        state.userRating = rating
    }),

    setValidations: action((state, validations) => {
        state.validations = validations
    }),

    setHint: action((state, payload) => {
        if (payload) {
            state.hint = payload.from
            state.hintsCounter += 1
            if (payload.to) {
                state.customArrows.push([payload.from, payload.to, '#C9F1AD', 0.8])
            }
        } else {
            state.hint = undefined
            state.customArrows = []
        }
    }),

    setHintAsync: thunk(async (actions, payload, helpers) => {
        try {
            const { getState } = helpers
            const state = getState()
            if (state.availableHints !== 0) {
                const result = await usedHint()
                actions.setHint(payload)
                actions.setAvailableHints(result.availableHints)
                analyticsManager.dispatchEvent('powerUpUsed', {
                    type: 'hints',
                    oldArgument: state.availableHints,
                    newArgument: result.availableHints,
                    argumentChange: state.availableHints - result.availableHints,
                    feature: 'dailyPuzzle',
                    puzzleId: state.puzzleId || '',
                })
            }
        } catch (error) {
            console.error('[DAILY_PUZZLE] Error in setHintAsync:', error)
        }
    }),

    clearHint: action((state) => {
        state.hint = undefined
        state.hintsCounter = 0
        state.customArrows = []
    }),

    setAvailableHints: action((state, payload) => {
        state.availableHints = payload
    }),

    setShowStreak: action((state, payload) => {
        state.showStreak = payload
    }),

    setStreak: action((state, payload) => {
        state.streak = payload
    }),

    setShowFeedbackDialog: action((state, payload) => {
        state.showFeedbackDialog = payload
    }),

    setFeedbackMessage: action((state, payload) => {
        state.feedbackMessage = payload
    }),

    setShowStreakAnimation: action((state, payload) => {
        state.showStreakAnimation = payload
    }),

    // Thunks

    establishListener: thunk((actions, _, { getStoreState, getStoreActions }) => {
        const { ws } = getStoreActions()
        const { isConnected } = getStoreState().ws
        if (isConnected) {
            ws.addListener({
                messageType: MessageType.PUZZLE_FEEDBACK,
                callback: (data) => {
                    const { streak, puzzleId } = getStoreState().dailyPuzzleView
                    console.log('[WS-GATEWAY] Received PUZZLE_FEEDBACK data:', data)
                    console.log('old streak:', streak)
                    console.log('show streak:', data.streak > streak)
                    if (ENABLE_DAILY_PUZZLE_FEEDBACK) {
                        if (data.streak > streak) {
                            actions.setShowStreak(true)
                        }
                        actions.setFeedbackMessage(data.message)
                        actions.setStreak(data.streak)
                        actions.setShowFeedbackDialog(true)

                        analyticsManager.dispatchEvent('feedbackPopupShown', {
                            puzzleId: puzzleId || '',
                            groupId: data.groupId,
                            percentileRaw: data.percentile,
                            feedbackScore: data.score,
                            streakCount: data.streak,
                            usedShowSolution: data.solutionShown,
                            dynamicText: data.message,
                            feature: 'dailyPuzzle',
                        })
                    } else {
                        if (data.streak > streak) {
                            actions.setShowStreakAnimation(true)
                        }
                        actions.setStreak(data.streak)
                    }
                },
            })
        }
    }),
}

export default DailyPuzzleViewModel
